"use client";

import React, {
    cloneElement,
    CSSProperties,
    ReactElement,
    ReactNode,
    SyntheticEvent,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
    Ref,
    Fragment,
    FC,
} from "react";
import {
    FadeInAnimationVariantType,
    formatSearchParamsToString,
    useSearchParamsQuery,
} from "@finbackoffice/site-core";
import { IModalForwardRefPropsV2, ModalsContextV2 } from "contexts";
import classNames from "classnames";
import { usePathname, useRouter } from "next/navigation";
import { createPortal } from "react-dom";
import FadeInAnimation from "../fade-in/FadeInAnimation";
import { IModalStyleConfig, MODAL_CONFIG_MAP, ModalStyleTypeEnum } from "./constants/modal-config";
import styles from "./modal.module.sass";
import ModalCloseButton, { ModalCloseButtonType } from "./components/ModalCloseButton";

interface IModalProps {
    children: ReactElement<any>;
    styleClass?: string;
    type: string;
    animateVariant: FadeInAnimationVariantType;
    styleConfigType?: ModalStyleTypeEnum;
    onAnimationEnd?: (visible: boolean) => void;
    customStyleConfig?: Partial<IModalStyleConfig>;
    closeButtonVariant?: ModalCloseButtonType;
    closeButton?: ReactNode;
    mask?: boolean;
    maskClosable?: boolean;
    keyboard?: boolean;
    onClose?: (e?: SyntheticEvent | KeyboardEvent) => void;
    position?: { top: number };
    alignMiddle?: boolean;
    priority?: boolean;
    containerElementId?: string;
    ref?: Ref<IModalForwardRefPropsV2>;
}

const Modal: FC<IModalProps> = ({
    children,
    styleClass,
    type,
    styleConfigType,
    customStyleConfig,
    animateVariant,
    closeButton,
    closeButtonVariant,
    onClose,
    mask = true,
    maskClosable = true,
    keyboard = true,
    priority,
    alignMiddle,
    position,
    containerElementId,
    ref,
    onAnimationEnd,
}) => {
    const [priorityOpened, setPriorityOpened] = useState(false);
    const [containerElement, setContainerElement] = useState<HTMLElement | null>(null);
    const [inlineStyle, setInlineStyle] = useState<CSSProperties>({});
    const [fadeInAnimationType, setFadeInAnimationType] = useState(animateVariant);
    const fadeInAnimationRef = useRef<{
        clearVisible: () => Promise<void>;
        forceOpen: () => void;
    } | null>(null);
    const isClosingRef = useRef(false);
    const router = useRouter();
    const pathname = usePathname();
    const { query } = useSearchParamsQuery();
    const { type: typeParam } = query;
    const modalRef = useRef<HTMLDivElement | null>(null);
    const touchStart = useRef(0);
    const isTouchMoving = useRef(false);

    const { setCurrentModal, currentModal, previousModal, clearCurrentModal, clearPrevModal } =
        useContext(ModalsContextV2);

    const styleConfig = useMemo<Partial<IModalStyleConfig>>(() => {
        const config =
            styleConfigType !== undefined ? MODAL_CONFIG_MAP[styleConfigType] : undefined;
        return { ...config, ...customStyleConfig };
    }, [customStyleConfig, styleConfigType]);

    const getContainerElement = useCallback(() => {
        let result: HTMLElement | null = null;
        if (document) {
            const id = containerElementId || (priority ? "modal-priority" : "modal-root");
            result = document.getElementById(id);
            if (containerElementId && result === null) {
                const newContainer = document.createElement("div");
                newContainer.id = containerElementId;
                document.body.appendChild(newContainer);
                return newContainer;
            }
        }
        return result;
    }, [containerElementId, priority]);

    const isOpened = useMemo(
        () => type === (currentModal || previousModal) || priorityOpened,
        [currentModal, previousModal, priorityOpened, type],
    );

    const open = useCallback(
        (animationVariant?: FadeInAnimationVariantType) => {
            if (animationVariant) {
                setFadeInAnimationType(animationVariant);
            }
            if (priority) {
                setPriorityOpened(true);
            } else {
                setCurrentModal(type);
            }

            if (isClosingRef.current) {
                fadeInAnimationRef.current?.forceOpen();
                isClosingRef.current = false;
            }
        },
        [priority, setCurrentModal, type],
    );

    const close = useCallback(
        async (e?: SyntheticEvent) => {
            if (!isClosingRef.current) {
                isClosingRef.current = true;

                if (typeParam === type) {
                    const q = { ...query };
                    delete q.type;
                    delete q.directory;
                    router.replace(`${pathname}?${formatSearchParamsToString(q)}`);
                }

                await fadeInAnimationRef.current?.clearVisible();

                const finish = () => {
                    isTouchMoving.current = false;
                    onClose?.(e);
                    isClosingRef.current = false;
                };

                if (type === previousModal) {
                    clearPrevModal();
                    finish();
                } else if (type === currentModal) {
                    clearCurrentModal();
                    finish();
                } else if (priorityOpened) {
                    setPriorityOpened(false);
                    finish();
                }
            }
        },
        [
            typeParam,
            type,
            previousModal,
            currentModal,
            priorityOpened,
            query,
            router,
            pathname,
            onClose,
            clearPrevModal,
            clearCurrentModal,
        ],
    );

    const onKeydownHandler = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                void close();
            }
        },
        [close],
    );

    useEffect(() => {
        if (currentModal === type || priorityOpened) {
            document.addEventListener("keydown", onKeydownHandler, false);
        }
        return () => {
            document.removeEventListener("keydown", onKeydownHandler, false);
        };
    }, [type, currentModal, priorityOpened, onKeydownHandler]);

    useEffect(() => {
        if (previousModal && type === previousModal) {
            void close();
        }
    }, [close, previousModal, type]);

    useEffect(() => {
        setContainerElement(getContainerElement());
        return () => {
            if (containerElementId) document.getElementById(containerElementId)?.remove();
        };
    }, [getContainerElement, containerElementId]);

    const forwardedProps = useMemo(
        () => ({
            open,
            close,
            setStyle: (style: CSSProperties) => setInlineStyle(style),
            isOpened,
        }),
        [open, close, isOpened],
    );

    // hook into the new ref prop
    useImperativeHandle(ref, () => forwardedProps);

    const onTouchStart = (event: any): void => {
        touchStart.current = event.touches[0].pageX;
    };

    const onTouchMove = useCallback(
        (event: any): void => {
            if (
                (fadeInAnimationType === "left" &&
                    touchStart.current - event.touches[0].pageX > 60) ||
                (fadeInAnimationType === "right" &&
                    touchStart.current - event.touches[0].pageX < -60)
            ) {
                if (!isTouchMoving.current) {
                    isTouchMoving.current = true;
                    setCurrentModal(null);
                }
            }
        },
        [fadeInAnimationType, setCurrentModal],
    );

    useEffect(() => {
        const node = modalRef.current;
        if (currentModal === type && keyboard) {
            node?.addEventListener("touchstart", onTouchStart, { passive: true });
            node?.addEventListener("touchmove", onTouchMove, { passive: true });
        } else {
            node?.removeEventListener("touchstart", onTouchStart);
            node?.removeEventListener("touchmove", onTouchMove);
        }
        return () => {
            node?.removeEventListener("touchstart", onTouchStart);
            node?.removeEventListener("touchmove", onTouchMove);
        };
    }, [currentModal, type, keyboard, onTouchMove]);

    const onBackdropClick = useCallback(() => {
        if (maskClosable) {
            void close();
            /* if (priority) setPriorityOpened(false);
            else setCurrentModal(null); */
        }
    }, [close, maskClosable]);

    const handleAnimationEnd = useCallback(
        (target: HTMLElement, visible: boolean) => {
            const testId = target.dataset.testid || target.getAttribute("data-testid");
            if (testId?.includes(type) && onAnimationEnd) {
                onAnimationEnd(visible);
            }
        },
        [onAnimationEnd, type],
    );

    if (!containerElement) return null;

    if (currentModal === type || previousModal === type || priorityOpened) {
        return createPortal(
            <FadeInAnimation
                ref={fadeInAnimationRef}
                variant={fadeInAnimationType}
                onAnimationEnd={handleAnimationEnd}
                scopedClassnames={{
                    variant: styles[fadeInAnimationType],
                    visible: styles.visible,
                    notVisible: styles["not-visible"],
                }}>
                <section
                    style={position ? { top: position.top } : undefined}
                    className={classNames(styles.container, "modal-container-base", styleClass)}>
                    {mask && (
                        <div
                            data-testid="modal-backdrop"
                            className={styles.backdrop}
                            onClick={onBackdropClick}
                        />
                    )}
                    <section
                        data-testid={`${type}-modal`}
                        className={classNames(
                            styles.modal,
                            styles.modalPositionAbsolute,
                            alignMiddle && styles.alignMiddle,
                            styleConfig.position && styles.position,
                            styleConfig.position && styles[`${styleConfig.position}`],
                            styleConfig.width && styles.width,
                            styleConfig.width && styles[`${styleConfig.width}`],
                            styleConfig.height && styles.height,
                            styleConfig.height && styles[`${styleConfig.height}`],
                            styleConfig.background && styles.background,
                            styleConfig.background && styles[`${styleConfig.background}`],
                            styleConfig.boxShadow && styles.boxshadow,
                            styleConfig.boxShadow && styles[`${styleConfig.boxShadow}`],
                            styleConfig.borderRadius && styles.borderradius,
                            styleConfig.borderRadius && styles[`${styleConfig.borderRadius}`],
                            styleConfig.overflow && styles.overflow,
                            styleConfig.overflow && styles[`${styleConfig.overflow}`],
                            styleConfig.direction && styles.direction,
                            styleConfig.direction && styles[`${styleConfig.direction}`],
                        )}
                        ref={modalRef}
                        style={inlineStyle}>
                        {closeButton &&
                            (typeof closeButton === "boolean" ? (
                                <ModalCloseButton variant={closeButtonVariant} onClick={close} />
                            ) : (
                                closeButton
                            ))}
                        {typeof children.type !== "string" && children.type !== Fragment
                            ? cloneElement(children, forwardedProps)
                            : children}
                    </section>
                </section>
            </FadeInAnimation>,
            containerElement,
        );
    }

    return null;
};

export default Modal;
